import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import Box from '@mui/material/Box';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { AttachmentStateDto } from '@/api/generated';
import {
  NewEmailAttachmentModel,
  UUID,
} from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import {
  NewEmailAttachmentsLoadingIconStyles,
  NewEmailAttachmentsTrashIconStyles,
  NewEmailAttachmentsTrashIconWrapperStyles,
} from '@/components/emailWindow/components/attachments/styles/NewEmailAttachments.styles.ts';
import { useGetAttachmentState } from '@/components/emailWindow/components/attachments/util/useGetAttachmentState.util.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { EmailAttachment } from '@/views/emails/components/attachmentItem/EmailAttachment.comp.tsx';

interface Props {
  attachment: NewEmailAttachmentModel;
  emailId: string;
  onDeleteAttachment: (externalAttachmentId: string, uuid: UUID) => Promise<void>;
  onSetAttachmentReady: (attachmentId: string) => void;
}

export const NewEmailAttachmentsItem: React.FC<Props> = props => {
  const { attachment, emailId, onDeleteAttachment, onSetAttachmentReady } = props;
  const { getAttachmentState } = useGetAttachmentState();
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body.attachment');
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);

  const checkUploadStatus = async (attachmentId: string) => {
    const intervalId = setInterval(async () => {
      const response = await getAttachmentState({
        attachmentId,
        emailId,
      });

      if (response === AttachmentStateDto.Error) {
        clearInterval(intervalId);
        toast(t('toast.employeesUploadedGeneralError'), ToastConfig.error);
        return;
      }

      if (response !== AttachmentStateDto.New) {
        clearInterval(intervalId);
        onSetAttachmentReady(attachmentId);
      }
    }, 2500);
  };

  useEffect(() => {
    if (attachment.isReady || attachment.attachmentId === undefined) {
      return;
    }
    void checkUploadStatus(attachment.attachmentId);
  }, []);

  const handleDeleteAttachment = async () => {
    if (attachment.externalAttachmentId === undefined) {
      throw new Error('Brak identyfikatora załącznika. Prosimy zgłosić błąd.');
    }
    setIsBeingDeleted(true);
    try {
      await onDeleteAttachment(attachment.externalAttachmentId, attachment.uuid);
    } finally {
      setIsBeingDeleted(false);
    }
  };

  return (
    <EmailAttachment isReady={attachment.isReady} name={attachment.name}>
      {attachment.isReady && !isBeingDeleted ? (
        <Box onClick={handleDeleteAttachment} sx={NewEmailAttachmentsTrashIconWrapperStyles}>
          <DeleteForeverOutlinedIcon sx={NewEmailAttachmentsTrashIconStyles} />
        </Box>
      ) : (
        <LoopIcon sx={NewEmailAttachmentsLoadingIconStyles} />
      )}
    </EmailAttachment>
  );
};
